import { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSaveFuel } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { FuelCategory, FuelInput, FuelPaymentMethod, FuelTransaction } from '../../types';
import { EUROPE_COUNTRIES, FUEL_CATEGORY_LABELS, FUEL_PAYMENT_LABELS, formatCurrency, toDateInput } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { CarSelect, DriverSelect } from './EntitySelects';
import { PersonModeToggle, type PersonMode } from './PersonModeToggle';
import { t } from '../../i18n';

interface Values {
  car_id: string;
  transaction_date: string;
  transaction_time: string;
  product: string;
  category: FuelCategory;
  quantity: string;
  amount_gross: string;
  vat_rate: string;
  payment_method: FuelPaymentMethod;
  driver_id: string;
  person_name: string;
  country: string;
  station: string;
  city: string;
  odometer: string;
  notes: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  transaction?: FuelTransaction | null;
}

// Product names as the card export writes them, so manual and imported refuelling group together
const PRODUCTS = ['Nafta', 'Prémiová nafta', 'Natural 95', 'Prémiový Natural 95', 'Natural 98', 'LPG', 'Ad Blue čerpané'];
const COUNTRY_OPTIONS = Object.entries(EUROPE_COUNTRIES).map(([code, name]) => ({ value: code, label: `${code} – ${name}` }));
const toNumber = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')));

export function FuelFormModal({ isOpen, onClose, transaction }: Props) {
  const save = useSaveFuel();
  const [personMode, setPersonMode] = useState<PersonMode>('list');
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Values>();

  useLayoutEffect(() => {
    if (!isOpen) return;
    setPersonMode(transaction?.person_name ? 'name' : 'list');
    reset({
      car_id: transaction ? String(transaction.car_id) : '',
      transaction_date: transaction?.transaction_date ?? toDateInput(),
      transaction_time: transaction?.transaction_time?.slice(0, 5) ?? '',
      product: transaction?.product ?? 'Nafta',
      category: transaction?.category ?? 'fuel',
      quantity: transaction?.quantity != null ? String(transaction.quantity) : '',
      amount_gross: transaction?.amount_gross != null ? String(transaction.amount_gross) : transaction ? String(transaction.amount_net) : '',
      vat_rate: transaction?.vat_rate != null ? String(transaction.vat_rate) : '23',
      payment_method: transaction?.payment_method ?? 'cash',
      driver_id: transaction?.driver_id ? String(transaction.driver_id) : '',
      person_name: transaction?.person_name ?? '',
      country: transaction?.country ?? 'SK',
      station: transaction?.station ?? '',
      city: transaction?.city ?? '',
      odometer: transaction?.odometer != null ? String(transaction.odometer) : '',
      notes: transaction?.notes ?? '',
    });
  }, [isOpen, transaction, reset]);

  const gross = toNumber(watch('amount_gross') ?? '');
  const vat = toNumber(watch('vat_rate') ?? '') ?? 0;
  const net = gross != null && !Number.isNaN(gross) ? gross / (1 + vat / 100) : null;

  const onProduct = (product: string) => {
    const text = product.toLowerCase();
    setValue('category', text.includes('ad blue') || text.includes('adblue') ? 'adblue' : PRODUCTS.includes(product) ? 'fuel' : watch('category'));
  };

  const onSubmit = async (v: Values) => {
    const data: FuelInput = {
      car_id: Number(v.car_id),
      transaction_date: v.transaction_date,
      transaction_time: v.transaction_time || null,
      product: v.product.trim(),
      category: v.category,
      quantity: toNumber(v.quantity),
      amount_gross: toNumber(v.amount_gross) ?? 0,
      vat_rate: toNumber(v.vat_rate) ?? 0,
      payment_method: v.payment_method,
      driver_id: personMode === 'list' && v.driver_id ? Number(v.driver_id) : null,
      person_name: personMode === 'name' ? v.person_name.trim() || null : null,
      country: v.country || null,
      station: v.station.trim() || null,
      city: v.city.trim() || null,
      odometer: toNumber(v.odometer),
      notes: v.notes.trim() || null,
    };
    try {
      await save.mutateAsync({ id: transaction?.id, data });
      toast.success(transaction ? t('Tankovanie bolo uložené') : t('Tankovanie bolo pridané'));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Tankovanie sa nepodarilo uložiť')));
    }
  };

  const payments = Object.entries(FUEL_PAYMENT_LABELS)
    // The fuel card is what the import brings in; it stays selectable only on imported rows
    .filter(([value]) => value !== 'fuel_card' || transaction?.payment_method === 'fuel_card')
    .map(([value, label]) => ({ value, label }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={save.isPending}
      size="lg"
      title={transaction ? t('Upraviť tankovanie') : t('Nové tankovanie')}
      description={!transaction ? t('Pre tankovanie zaplatené v hotovosti alebo inou kartou – tankovanie zo Shell kariet sa importuje.') : undefined}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="fuel-form" loading={save.isPending}>
            {transaction ? t('Uložiť') : t('Pridať')}
          </Button>
        </>
      }
    >
      <form id="fuel-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <CarSelect label={t('Vozidlo')} required error={errors.car_id?.message} {...register('car_id', { required: t('Vyberte vozidlo') })} />
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('Dátum')} type="date" required error={errors.transaction_date?.message} {...register('transaction_date', { required: t('Zadajte dátum') })} />
          <Input label={t('Čas')} type="time" {...register('transaction_time')} />
        </div>
        <div>
          <Input
            label={t('Produkt')}
            required
            list="fuel-products"
            error={errors.product?.message}
            {...register('product', { required: t('Zadajte produkt'), onChange: (e) => onProduct(e.target.value) })}
          />
          <datalist id="fuel-products">
            {PRODUCTS.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>
        <Select
          label={t('Druh')}
          options={Object.entries(FUEL_CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
          hint={t('Do spotreby sa počíta iba palivo')}
          {...register('category')}
        />
        <Input label={t('Množstvo (l)')} type="number" step="0.01" {...register('quantity')} />
        <Select label={t('Platba')} options={payments} {...register('payment_method')} />
        <Input
          label={t('Suma s DPH (€)')}
          type="number"
          step="0.01"
          required
          error={errors.amount_gross?.message}
          {...register('amount_gross', { required: t('Zadajte sumu') })}
        />
        <Input
          label={t('Sadzba DPH (%)')}
          type="number"
          step="1"
          min={0}
          max={100}
          hint={net != null ? t('Bez DPH: {net} – táto suma ide do nákladov', { net: formatCurrency(net) }) : undefined}
          {...register('vat_rate')}
        />
        <div className="sm:col-span-2">
          <p className="mb-1.5 text-sm font-medium text-secondary-700">{t('Kto tankoval')}</p>
          <PersonModeToggle value={personMode} onChange={setPersonMode} />
          {personMode === 'list' ? (
            <DriverSelect aria-label={t('Pracovník')} onlyActive={false} placeholder={t('Neurčený')} {...register('driver_id')} />
          ) : (
            <Input aria-label={t('Meno a priezvisko')} placeholder={t('Meno a priezvisko')} {...register('person_name')} />
          )}
        </div>
        <Select label={t('Krajina')} options={COUNTRY_OPTIONS} {...register('country')} />
        <Input label={t('Čerpacia stanica')} placeholder={t('napr. Shell Žilina')} {...register('station')} />
        <Input label={t('Mesto')} {...register('city')} />
        <Input label={t('Stav tachometra (km)')} type="number" min={0} {...register('odometer')} />
        <Textarea label={t('Poznámka')} rows={2} wrapperClassName="sm:col-span-2" {...register('notes')} />
      </form>
    </Modal>
  );
}
